import React, { useContext, useEffect, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { Alert, Button, Stack, Typography } from '@mui/material';

import { legacyLensUrl } from '~/pages/AskRichardUnified/legacyLensUrl';

import { encodedJsonBody } from '~/hooks/apis/reporting/encodedJsonBody';
import { useGetReportingLeLens } from '~/hooks/apis/reporting/useGetReportingLeLens';

import { requestWithTimeout } from '~/utils/requestWithTimeout';
import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import LensStructuralProposalPreview from './LensStructuralProposalPreview';
import { type LensProposal, globalLensProposalKey, lensProposalKey } from './lensProposal';
import { ProposalCollapse } from './proposalCollapse';
import {
  type ReviewCandidateState,
  canActOnReviewCandidate,
  rememberReviewCandidate,
  reviewCandidateRequest,
} from './reviewLensCandidate';

export default function LensReviewProposalPreview({
  proposal,
  globalDraft = false,
  globalSuperseded = false,
  hasUnsavedChanges = false,
  onDiscard,
}: {
  proposal: LensProposal;
  globalDraft?: boolean;
  globalSuperseded?: boolean;
  hasUnsavedChanges?: boolean;
  onDiscard?: () => void;
}) {
  const client = useQueryClient();
  const { data, isPending } = useGetReportingLeLens(proposal.lensId);
  const lens = data?.lens;
  const canPropose = data?.access.canPropose ?? false;
  const [terminal, setTerminal] = useState<'submitted' | 'discarded' | undefined>(() => {
    const status = client.getQueryData<LensProposal>(globalLensProposalKey(proposal))?.status ?? proposal.status;
    return status === 'submitted' || status === 'discarded' ? status : undefined;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const reportCollapse = useContext(ProposalCollapse);
  const latest = useQuery<LensProposal | null>({
    queryKey: lensProposalKey(proposal.lensId),
    queryFn: () => null,
    enabled: false,
  });
  const superseded = globalDraft
    ? globalSuperseded
    : Boolean(latest.data && latest.data.createdAt > proposal.createdAt);
  const call = async (action: 'inspect' | 'discard' | 'submit') => {
    const request = reviewCandidateRequest(proposal, action);
    const response = await requestWithTimeout<ReviewCandidateState & { proposal?: { id: string; status: string } }>(
      resolveReportingLeLensUrl(request.path),
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: encodedJsonBody(request.body) },
      'Could not update the proposal. Try again.',
    );
    if (response.purpose !== 'review_proposal') throw new Error('Could not verify this owner-review draft.');
    return response;
  };
  const candidate = useQuery({
    queryKey: ['lens-review-candidate', proposal.lensId, proposal.candidateId, lens?.version],
    enabled: Boolean(lens && proposal.candidateId && canPropose && !terminal && !superseded),
    retry: false,
    queryFn: () => call('inspect'),
  });
  const finish = (status: 'discarded' | 'submitted', submittedProposalId?: string) => {
    rememberReviewCandidate(client, { ...proposal, status, submittedProposalId });
    setTerminal(status);
    if (status === 'discarded') onDiscard?.();
  };
  useEffect(() => {
    if (candidate.data?.status === 'submitted' || candidate.data?.status === 'discarded') {
      rememberReviewCandidate(client, {
        ...proposal,
        status: candidate.data.status,
        submittedProposalId: candidate.data.submittedProposalId,
      });
      setTerminal(candidate.data.status);
    } else if (!terminal && !superseded) {
      rememberReviewCandidate(client, proposal);
    }
  }, [candidate.data, client, proposal, superseded, terminal]);
  useEffect(() => reportCollapse?.(Boolean(terminal) || superseded), [reportCollapse, superseded, terminal]);
  const act = async (action: 'discard' | 'submit') => {
    if (!canPropose || busy || hasUnsavedChanges || !canActOnReviewCandidate(candidate.data, action)) return;
    setError('');
    setBusy(true);
    try {
      const response = await call(action);
      if (action === 'submit') {
        if (response.status !== 'submitted' || !response.proposal?.id || response.proposal.status !== 'pending') {
          throw new Error('The proposal was not submitted. Try again.');
        }
        finish('submitted', response.proposal.id);
        await client.invalidateQueries({ queryKey: ['lens-collaboration', proposal.lensId] });
      } else {
        if (response.status !== 'discarded') throw new Error('The proposal was not discarded. Try again.');
        finish('discarded');
      }
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Could not update the proposal');
    } finally {
      setBusy(false);
    }
  };
  if (isPending) return <Typography>Checking the Lens and your proposal access…</Typography>;
  if (!lens) return <Alert severity="error">Could not load this Lens.</Alert>;
  if (!canPropose)
    return <Alert severity="info">You do not have permission to propose changes to “{lens.title}”.</Alert>;
  if (terminal)
    return (
      <Stack spacing={0.5} data-testid="lens-review-proposal-receipt">
        <Typography>
          {terminal === 'submitted'
            ? `Proposal submitted for the owner of “${lens.title}” to review.`
            : `Discarded proposal for “${lens.title}”.`}
        </Typography>
        <Button href={legacyLensUrl(lens.id, '')} target="_blank" rel="noreferrer" sx={{ alignSelf: 'flex-start' }}>
          Open Lens
        </Button>
      </Stack>
    );
  if (superseded) return <Typography>A newer proposal is available below.</Typography>;
  if (hasUnsavedChanges)
    return <Alert severity="info">Save or discard your current card edits before reviewing this proposal.</Alert>;
  if (candidate.error)
    return (
      <Alert severity="error" action={<Button onClick={() => void candidate.refetch()}>Retry</Button>}>
        {candidate.error.message}
      </Alert>
    );
  if (
    candidate.data?.status === 'conflict' ||
    (lens.version !== proposal.baseVersion && candidate.data?.status === 'current')
  )
    return (
      <Stack spacing={1}>
        <Alert severity="warning">
          The Lens changed after this proposal. Ask for an updated proposal before submitting it.
        </Alert>
        {error && <Alert severity="error">{error}</Alert>}
        {canActOnReviewCandidate(candidate.data, 'discard') && (
          <Button disabled={busy} onClick={() => void act('discard')}>
            Discard
          </Button>
        )}
      </Stack>
    );
  if (candidate.data?.status === 'expired')
    return <Alert severity="info">This proposal expired. Ask for it again.</Alert>;
  if (candidate.isPending || candidate.isFetching || !candidate.data?.definition)
    return <Typography>Loading the proposed changes…</Typography>;
  return (
    <Stack spacing={1.5} data-testid="lens-review-proposal-preview">
      <Typography variant="subtitle2">Proposed changes to “{lens.title}”</Typography>
      <LensStructuralProposalPreview lens={lens} definition={candidate.data.definition} />
      {candidate.data.proposalSummary && (
        <Stack spacing={0.25}>
          <Typography variant="subtitle2">Proposal summary</Typography>
          <Typography variant="body2">{candidate.data.proposalSummary}</Typography>
        </Stack>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {!candidate.data.canSubmit && !candidate.data.canDiscard && (
        <Typography variant="body2" color="text.secondary">
          Only the proposal’s author can submit or discard this draft.
        </Typography>
      )}
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          disabled={busy || !canActOnReviewCandidate(candidate.data, 'submit')}
          onClick={() => void act('submit')}
        >
          Submit proposal
        </Button>
        <Button
          disabled={busy || !canActOnReviewCandidate(candidate.data, 'discard')}
          onClick={() => void act('discard')}
        >
          Discard
        </Button>
        <Button href={legacyLensUrl(lens.id, '')} target="_blank" rel="noreferrer">
          Open Lens
        </Button>
      </Stack>
    </Stack>
  );
}
